import React, { useEffect, useMemo, useState, memo } from "react";
import {
  Search,
  LayoutGrid,
  Table2,
  Trophy,
  Sword,
  Skull,
  Target,
  Pickaxe,
  Coins,
  Clock3,
  Footprints,
  Zap,
  Shield,
  Activity,
  X,
  Crown,
  Medal,
  Users,
  RefreshCw,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";

import SectionHeader from "@/components/SectionHeader";
import {
  fetchLeaderboardSeason,
  fetchLeaderboardPlayerEvents,
} from "@/lib/api";

const PRIORITY_RANKS = [
  "OWNER",
  "BEDROCK",
  "LEGEND",
  "ADMIN",
  "IMMORTAL",
  "TITAN",
  "WARRIOR",
  "RAIDER",
  "HUNTER",
  "MEDIA",
];

const RANK_STYLES = {
  OWNER: {
    gradient: "linear-gradient(145deg,#111827,#7f1d1d)",
    glow: "rgba(127,29,29,.45)",
  },
  BEDROCK: {
    gradient: "linear-gradient(145deg,#111827,#78350f)",
    glow: "rgba(120,53,15,.45)",
  },
  LEGEND: {
    gradient: "linear-gradient(145deg,#111827,#1e40af)",
    glow: "rgba(30,64,175,.45)",
  },
  HUNTER: {
    gradient: "linear-gradient(145deg,#111827,#be185d)",
    glow: "rgba(190,24,93,.45)",
  },
  RAIDER: {
    gradient: "linear-gradient(145deg,#111827,#0ea5e9)",
    glow: "rgba(14,165,233,.45)",
  },
  WARRIOR: {
    gradient: "linear-gradient(145deg,#111827,#166534)",
    glow: "rgba(22,101,52,.45)",
  },
  TITAN: {
    gradient: "linear-gradient(145deg,#111827,#ca8a04)",
    glow: "rgba(202,138,4,.45)",
  },
  IMMORTAL: {
    gradient: "linear-gradient(145deg,#111827,#6d28d9)",
    glow: "rgba(109,40,217,.45)",
  },
  ADMIN: {
    gradient: "linear-gradient(145deg,#111827,#4c1d95)",
    glow: "rgba(76,29,149,.45)",
  },
  MEDIA: {
    gradient: "linear-gradient(145deg,#111827,#065f46)",
    glow: "rgba(6,95,70,.45)",
  },
};

const TAG_STYLES = {
  "⛏ Miner": {
    icon: Pickaxe,
    className: "text-emerald-300 bg-emerald-400/10 border-emerald-400/20",
  },
  "⚔ Warrior": {
    icon: Sword,
    className: "text-red-300 bg-red-400/10 border-red-400/20",
  },
  "🧭 Explorer": {
    icon: Footprints,
    className: "text-cyan-300 bg-cyan-400/10 border-cyan-400/20",
  },
  "🌍 Survivor": {
    icon: Trophy,
    className: "text-amber-300 bg-amber-400/10 border-amber-400/20",
  },
};

const LEADERBOARD_CATEGORIES = [
  { key: "rating", label: "Rating", icon: Trophy },
  { key: "kills", label: "Kills", icon: Sword },
  { key: "deaths", label: "Deaths", icon: Skull },
  { key: "kd", label: "K/D", icon: Activity },
  { key: "damage_dealt", label: "Damage Dealt", icon: Target },
  { key: "damage_taken", label: "Damage Taken", icon: Shield },
  { key: "blocks_mined", label: "Blocks Mined", icon: Pickaxe },
  { key: "jumps", label: "Jumps", icon: Footprints },
  { key: "money", label: "Money", icon: Coins },
  { key: "event", label: "Event Points", icon: Zap },
  { key: "playtime", label: "Playtime", icon: Clock3 },
  { key: "distance", label: "Distance", icon: Footprints },
];

const number = (value) => Number(value || 0).toLocaleString("en-IN");

const decimal = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

const getPriority = (prefix) => {
  const index = PRIORITY_RANKS.indexOf(
    String(prefix || "").trim().toUpperCase()
  );
  return index === -1 ? 999 : index;
};

const getRankStyle = (prefix) =>
  RANK_STYLES[String(prefix || "").trim().toUpperCase()] || {
    gradient: "linear-gradient(145deg,#111827,#1f2937)",
    glow: "rgba(255,255,255,.12)",
  };

const getTagStyle = (tag) => TAG_STYLES[tag] || TAG_STYLES["🌍 Survivor"];

const getUsername = (player) => player?.name || player?.username || "Unknown";

function Stat({ icon: Icon, label, value, accent = false }) {
  return (
    <div className="rounded-xl border border-white/10 bg-black/20 p-3">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/45">
        <Icon size={13} />
        {label}
      </div>

      <div
        className={`mt-1 text-lg font-bold ${
          accent ? "text-[#FFBF00]" : "text-white"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function RatingBar({ rating }) {
  const value = Math.max(0, Math.min(100, Number(rating || 0)));

  return (
    <div className="mt-3">
      <div className="mb-1 flex justify-between text-[10px] uppercase tracking-widest text-white/45">
        <span>LostDC Rating</span>
        <span>{value}/100</span>
      </div>

      <div className="h-2 overflow-hidden rounded-full bg-black/40">
        <div
          className="h-full rounded-full bg-gradient-to-r from-yellow-500 via-amber-300 to-yellow-500 transition-all duration-700"
          style={{ width: `${value}%` }}
        />
      </div>
    </div>
  );
}

/* PLAYER CARD WITH SQUARE AVATAR & PROMINENT BIG RANK */
const PlayerCard = memo(function PlayerCard({ player, onClick, onEventClick }) {
  const username = getUsername(player);
  const rankStyle = getRankStyle(player.prefix);
  const tagStyle = getTagStyle(player.tag);
  const TagIcon = tagStyle.icon;

  return (
    <button
      onClick={() => onClick(player)}
      className="group relative overflow-hidden rounded-2xl border border-white/10 p-[1px] text-left transition-all duration-300 hover:-translate-y-2 hover:border-white/20"
      style={{
        background: rankStyle.gradient,
        boxShadow: `0 15px 45px ${rankStyle.glow}`,
      }}
    >
      <div className="relative rounded-[15px] bg-black/20 p-5 backdrop-blur-sm">
        {/* Very Big & Nice Rank Badge */}
        <div className="absolute right-3 top-3 flex items-center justify-center rounded-xl border border-white/20 bg-black/60 px-3 py-1 shadow-2xl backdrop-blur-md">
          <span className="text-xl sm:text-2xl font-black text-yellow-400 tracking-wider drop-shadow-md">
            #{player.rank || "—"}
          </span>
        </div>

        <div className="flex items-center gap-4">
          {/* Square Avatar */}
          <img
            src={`https://mc-heads.net/avatar/${encodeURIComponent(
              username
            )}/80`}
            alt={username}
            className="h-16 w-16 rounded-2xl border-2 border-white/20 bg-black/30 shadow-xl transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
          />

          <div className="min-w-0 pr-16">
            <div className="truncate text-xl font-black text-white">
              {username}
            </div>

            {player.prefix ? (
              <div className="mt-1 text-xs font-semibold uppercase tracking-widest text-white/60">
                {player.prefix}
              </div>
            ) : (
              <div className="mt-1 text-xs uppercase tracking-widest text-white/35">
                LostDC Player
              </div>
            )}
          </div>
        </div>

        <div className="mt-5 rounded-xl border border-white/10 bg-black/20 p-3">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/50">
              <Trophy size={14} />
              Rating
            </span>

            <span className="text-2xl font-black text-[#FFBF00]">
              {player.rating}
              <span className="text-sm text-white/40">/100</span>
            </span>
          </div>

          <RatingBar rating={player.rating} />
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2">
          <Stat icon={Sword} label="Kills" value={number(player.kills)} />
          <Stat icon={Skull} label="Deaths" value={number(player.deaths)} />
          <Stat icon={Activity} label="K/D" value={decimal(player.kd)} />
          <Stat icon={Coins} label="Money" value={number(player.money)} />
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <span
            className={`inline-flex items-center gap-1.5 rounded-xl border px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest ${tagStyle.className}`}
          >
            <TagIcon size={12} />
            {player.tag || "🌍 Survivor"}
          </span>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onEventClick(player);
            }}
            className="inline-flex items-center gap-1.5 rounded-xl border border-yellow-400/20 bg-yellow-400/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-yellow-300 transition hover:border-yellow-400/40 hover:bg-yellow-400/20"
          >
            <Zap size={12} />
            {number(player.event)} Event Points
          </button>
        </div>

        <div className="mt-4 grid grid-cols-3 gap-2 border-t border-white/10 pt-4 text-center">
          <div>
            <div className="text-sm font-bold text-white">
              {number(player.blocks_mined)}
            </div>
            <div className="text-[9px] uppercase tracking-widest text-white/35">
              Mined
            </div>
          </div>

          <div>
            <div className="text-sm font-bold text-white">
              {decimal(player.playtime)}
            </div>
            <div className="text-[9px] uppercase tracking-widest text-white/35">
              Hours
            </div>
          </div>

          <div>
            <div className="text-sm font-bold text-white">
              {number(player.distance)}
            </div>
            <div className="text-[9px] uppercase tracking-widest text-white/35">
              Distance
            </div>
          </div>
        </div>
      </div>
    </button>
  );
});

/* MEMOIZED ROW FOR LEADERBOARD BUBBLE LIST */
const LeaderboardRow = memo(function LeaderboardRow({
  player,
  onPlayerClick,
  sort,
  getMetricValue,
}) {
  const username = getUsername(player);
  const isTopThree = player.rank <= 3;

  return (
    <div
      onClick={() => onPlayerClick(player)}
      className={`group flex cursor-pointer flex-col gap-4 rounded-2xl border p-4 sm:p-5 transition-all hover:-translate-y-0.5 ${
        player.rank === 1
          ? "border-yellow-400/50 bg-gradient-to-r from-yellow-500/10 via-black/40 to-black/40"
          : player.rank === 2
          ? "border-slate-300/40 bg-gradient-to-r from-slate-300/10 via-black/40 to-black/40"
          : player.rank === 3
          ? "border-orange-400/40 bg-gradient-to-r from-orange-400/10 via-black/40 to-black/40"
          : "border-white/10 bg-black/30 hover:border-white/20 hover:bg-white/[0.04]"
      }`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 sm:gap-4 min-w-0">
          {/* Very Big & Bold Rank Box */}
          <div
            className={`grid h-12 w-12 sm:h-14 sm:w-14 shrink-0 place-items-center rounded-2xl text-lg sm:text-2xl font-black shadow-inner ${
              player.rank === 1
                ? "bg-gradient-to-br from-yellow-300 to-yellow-500 text-black shadow-yellow-500/50"
                : player.rank === 2
                ? "bg-gradient-to-br from-slate-100 to-slate-400 text-black shadow-slate-400/50"
                : player.rank === 3
                ? "bg-gradient-to-br from-amber-500 to-orange-600 text-black shadow-orange-500/50"
                : "border border-white/20 bg-black/60 text-yellow-400"
            }`}
          >
            #{player.rank}
          </div>

          {/* Square Avatar */}
          <img
            src={`https://mc-heads.net/avatar/${encodeURIComponent(
              username
            )}/64`}
            alt={username}
            className="h-11 w-11 sm:h-12 sm:w-12 rounded-2xl border border-white/10 bg-black/40 shrink-0"
            loading="lazy"
          />

          <div className="min-w-0">
            <div className="truncate text-base sm:text-lg font-black text-white">
              {username}
            </div>

            <div className="flex items-center gap-2 text-[10px] text-white/40">
              <span>{player.prefix || "LostDC"}</span>
              <span>•</span>
              <span className="text-yellow-400 font-semibold">
                {player.rating} Rating
              </span>
            </div>
          </div>
        </div>

        <div className="text-right shrink-0">
          <div
            className={`text-lg sm:text-xl font-black ${
              isTopThree ? "text-yellow-300" : "text-white"
            }`}
          >
            {getMetricValue(player, sort)}
          </div>
          <div className="text-[9px] uppercase tracking-wider text-white/40">
            {LEADERBOARD_CATEGORIES.find((c) => c.key === sort)?.label}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-8 border-t border-white/10 pt-3">
        <div className="rounded-xl border border-white/5 bg-white/[0.02] p-2 text-center">
          <div className="text-[9px] uppercase tracking-wider text-white/40 flex items-center justify-center gap-1">
            <Sword size={10} /> Kills
          </div>
          <div className={`mt-0.5 text-xs font-bold ${sort === "kills" ? "text-yellow-300" : "text-white"}`}>
            {number(player.kills)}
          </div>
        </div>

        <div className="rounded-xl border border-white/5 bg-white/[0.02] p-2 text-center">
          <div className="text-[9px] uppercase tracking-wider text-white/40 flex items-center justify-center gap-1">
            <Skull size={10} /> Deaths
          </div>
          <div className={`mt-0.5 text-xs font-bold ${sort === "deaths" ? "text-yellow-300" : "text-white"}`}>
            {number(player.deaths)}
          </div>
        </div>

        <div className="rounded-xl border border-white/5 bg-white/[0.02] p-2 text-center">
          <div className="text-[9px] uppercase tracking-wider text-white/40 flex items-center justify-center gap-1">
            <Activity size={10} /> K/D
          </div>
          <div className={`mt-0.5 text-xs font-bold ${sort === "kd" ? "text-yellow-300" : "text-white"}`}>
            {decimal(player.kd)}
          </div>
        </div>

        <div className="rounded-xl border border-white/5 bg-white/[0.02] p-2 text-center">
          <div className="text-[9px] uppercase tracking-wider text-white/40 flex items-center justify-center gap-1">
            <Coins size={10} /> Money
          </div>
          <div className={`mt-0.5 text-xs font-bold ${sort === "money" ? "text-yellow-300" : "text-white"}`}>
            ${number(player.money)}
          </div>
        </div>

        <div className="rounded-xl border border-white/5 bg-white/[0.02] p-2 text-center">
          <div className="text-[9px] uppercase tracking-wider text-white/40 flex items-center justify-center gap-1">
            <Pickaxe size={10} /> Mined
          </div>
          <div className={`mt-0.5 text-xs font-bold ${sort === "blocks_mined" ? "text-yellow-300" : "text-white"}`}>
            {number(player.blocks_mined)}
          </div>
        </div>

        <div className="rounded-xl border border-white/5 bg-white/[0.02] p-2 text-center">
          <div className="text-[9px] uppercase tracking-wider text-white/40 flex items-center justify-center gap-1">
            <Target size={10} /> Damage
          </div>
          <div className={`mt-0.5 text-xs font-bold ${sort === "damage_dealt" ? "text-yellow-300" : "text-white"}`}>
            {number(player.damage_dealt)}
          </div>
        </div>

        <div className="rounded-xl border border-white/5 bg-white/[0.02] p-2 text-center">
          <div className="text-[9px] uppercase tracking-wider text-white/40 flex items-center justify-center gap-1">
            <Zap size={10} /> Event Pts
          </div>
          <div className={`mt-0.5 text-xs font-bold ${sort === "event" ? "text-yellow-300" : "text-yellow-400"}`}>
            {number(player.event)}
          </div>
        </div>

        <div className="rounded-xl border border-white/5 bg-white/[0.02] p-2 text-center">
          <div className="text-[9px] uppercase tracking-wider text-white/40 flex items-center justify-center gap-1">
            <Clock3 size={10} /> Playtime
          </div>
          <div className={`mt-0.5 text-xs font-bold ${sort === "playtime" ? "text-yellow-300" : "text-white"}`}>
            {decimal(player.playtime)}h
          </div>
        </div>
      </div>
    </div>
  );
});

/* LEADERBOARD LIST WITH CATEGORIES */
function LeaderboardBubbleList({
  players,
  onPlayerClick,
  sort,
  direction,
  onSort,
}) {
  const getMetricValue = (player, metric) => {
    switch (metric) {
      case "kd":
      case "playtime":
        return decimal(player[metric]);
      case "money":
        return `$${number(player[metric])}`;
      default:
        return number(player[metric]);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-white/10 bg-black/40 p-3 backdrop-blur-xl">
        <div className="mb-2 flex items-center justify-between text-xs text-white/50 px-1">
          <span className="font-bold uppercase tracking-wider text-[10px]">
            Ranking Category
          </span>
          <span className="flex items-center gap-1 text-[10px]">
            <ArrowUpDown size={10} /> Click category bubble to flip order
          </span>
        </div>

        <div className="no-scrollbar flex items-center gap-2 overflow-x-auto pb-1 pt-1">
          {LEADERBOARD_CATEGORIES.map(({ key, label, icon: Icon }) => {
            const isActive = sort === key;
            return (
              <button
                key={key}
                onClick={() => onSort(key)}
                className={`flex shrink-0 items-center gap-1.5 rounded-full border px-3.5 py-2 text-xs font-bold transition-all ${
                  isActive
                    ? "border-yellow-400 bg-yellow-400 text-black shadow-lg shadow-yellow-400/20"
                    : "border-white/10 bg-white/5 text-white/70 hover:border-white/20 hover:bg-white/10"
                }`}
              >
                <Icon size={13} />
                <span>{label}</span>
                {isActive &&
                  (direction === "desc" ? (
                    <ArrowDown size={12} />
                  ) : (
                    <ArrowUp size={12} />
                  ))}
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-3">
        {players.map((player) => (
          <LeaderboardRow
            key={`${getUsername(player)}-${player.rank}`}
            player={player}
            onPlayerClick={onPlayerClick}
            sort={sort}
            getMetricValue={getMetricValue}
          />
        ))}
      </div>
    </div>
  );
}

/* OPTIMIZED SCROLLING EVENT POINTS MODAL */
function EventPointsModal({ player, events, loading, onClose }) {
  const totalPoints = events.reduce(
    (sum, event) => sum + Number(event.points || 0),
    0
  );

  if (!player) return null;

  const username = getUsername(player);

  const formatDate = (value) => {
    if (!value) return "Date unavailable";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);

    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/80 p-4 backdrop-blur-md"
      onMouseDown={onClose}
    >
      <div
        className="relative flex max-h-[85vh] w-full max-w-2xl flex-col rounded-3xl border border-yellow-400/20 bg-[#080b12] shadow-2xl overflow-hidden"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Modal Fixed Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-white/10 p-5 sm:p-6 bg-black/40">
          <div className="flex items-center gap-4 pr-8">
            {/* Square Avatar */}
            <img
              src={`https://mc-heads.net/avatar/${encodeURIComponent(
                username
              )}/80`}
              alt={username}
              className="h-14 w-14 rounded-2xl border border-white/10 shrink-0"
            />

            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-yellow-400/70">
                Event Points
              </div>

              <div className="text-xl font-black text-white sm:text-2xl">
                {username}
              </div>

              <div className="text-xs text-white/40">
                Complete event point history
              </div>
            </div>
          </div>

          <button
            onClick={onClose}
            className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-white/10 bg-white/5 text-white/60 transition hover:bg-white/10 hover:text-white"
          >
            <X size={18} />
          </button>
        </div>

        {/* Modal Scrollable Container */}
        <div className="flex-1 overflow-y-auto p-5 sm:p-6 no-scrollbar space-y-5">
          <div className="rounded-2xl border border-yellow-400/20 bg-yellow-400/5 p-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-white/40">
                  Total Event Points
                </div>

                <div className="mt-1 text-3xl sm:text-4xl font-black text-yellow-300">
                  {number(totalPoints)}
                </div>
              </div>

              <Zap size={32} className="text-yellow-400" />
            </div>
          </div>

          <div>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-black uppercase tracking-widest text-white">
                Events Log
              </h3>

              <span className="text-xs text-white/35">
                {events.length} event{events.length === 1 ? "" : "s"}
              </span>
            </div>

            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((item) => (
                  <div
                    key={item}
                    className="h-20 animate-pulse rounded-2xl border border-white/10 bg-white/[0.03]"
                  />
                ))}
              </div>
            ) : events.length === 0 ? (
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-8 text-center">
                <Trophy size={32} className="mx-auto text-white/20" />

                <div className="mt-3 font-bold text-white/70">
                  No event points
                </div>

                <div className="mt-1 text-xs text-white/35">
                  This player has not received points from an event.
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {events.map((event) => (
                  <div
                    key={`${event.id}-${event.point_id}`}
                    className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 transition hover:border-yellow-400/20 hover:bg-white/[0.05]"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <Trophy
                            size={16}
                            className="shrink-0 text-yellow-400"
                          />

                          <div className="truncate font-bold text-white">
                            {event.name}
                          </div>
                        </div>

                        <div className="mt-2 flex flex-wrap gap-2 text-[10px] uppercase tracking-widest">
                          <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-white/45">
                            {formatDate(event.event_date)}
                          </span>

                          {event.placement != null && (
                            <span className="rounded-full border border-yellow-400/20 bg-yellow-400/10 px-2.5 py-1 text-yellow-300">
                              #{event.placement}
                            </span>
                          )}

                          {event.status && (
                            <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-white/45">
                              {event.status}
                            </span>
                          )}
                        </div>

                        {event.note && (
                          <div className="mt-3 text-xs text-white/50">
                            {event.note}
                          </div>
                        )}
                      </div>

                      <div className="shrink-0 text-right">
                        <div className="text-xl sm:text-2xl font-black text-yellow-300">
                          +{number(event.points)}
                        </div>

                        {event.max_points != null && (
                          <div className="text-[9px] uppercase tracking-widest text-white/30">
                            / {number(event.max_points)} max
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* PLAYER MODAL WITH SQUARE AVATAR & PROMINENT RANK */
function PlayerModal({ player, onClose, onEventClick }) {
  if (!player) return null;

  const username = getUsername(player);
  const rankStyle = getRankStyle(player.prefix);
  const tagStyle = getTagStyle(player.tag);
  const TagIcon = tagStyle.icon;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4 backdrop-blur-md"
      onMouseDown={onClose}
    >
      <div
        className="relative max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-3xl border border-white/10 shadow-2xl"
        style={{ background: rankStyle.gradient }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute right-4 top-4 z-10 grid h-10 w-10 place-items-center rounded-full border border-white/10 bg-black/30 text-white/70 transition hover:bg-white/10 hover:text-white"
        >
          <X size={18} />
        </button>

        <div className="p-6 sm:p-8">
          <div className="flex flex-col items-center gap-5 text-center sm:flex-row sm:text-left">
            {/* Square Avatar */}
            <img
              src={`https://mc-heads.net/avatar/${encodeURIComponent(
                username
              )}/120`}
              alt={username}
              className="h-28 w-28 rounded-2xl border-2 border-white/20 shadow-2xl"
            />

            <div className="flex-1">
              <div className="text-3xl font-black text-white sm:text-4xl">
                {username}
              </div>

              {player.prefix && (
                <div className="mt-2 text-sm font-bold uppercase tracking-widest text-white/60">
                  {player.prefix}
                </div>
              )}

              <div className="mt-3 flex flex-wrap justify-center gap-2 sm:justify-start">
                <span
                  className={`inline-flex items-center gap-2 rounded-xl border px-3 py-1.5 text-xs font-bold ${tagStyle.className}`}
                >
                  <TagIcon size={14} />
                  {player.tag}
                </span>

                <span className="rounded-xl border border-yellow-400/40 bg-yellow-400/20 px-3 py-1.5 text-xs font-black text-yellow-300">
                  Rank #{player.rank}
                </span>
              </div>
            </div>

            <div className="text-center">
              <div className="text-5xl font-black text-[#FFBF00]">
                {player.rating}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-white/40">
                Rating / 100
              </div>
            </div>
          </div>

          <RatingBar rating={player.rating} />

          <div className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Stat icon={Sword} label="Kills" value={number(player.kills)} />
            <Stat icon={Skull} label="Deaths" value={number(player.deaths)} />
            <Stat icon={Activity} label="K/D" value={decimal(player.kd)} />
            <Stat icon={Target} label="Damage Dealt" value={number(player.damage_dealt)} />
            <Stat icon={Shield} label="Damage Taken" value={number(player.damage_taken)} />
            <Stat icon={Pickaxe} label="Blocks Mined" value={number(player.blocks_mined)} />
            <Stat icon={Footprints} label="Jumps" value={number(player.jumps)} />
            <Stat icon={Coins} label="Money" value={number(player.money)} accent />
            <button
              type="button"
              onClick={() => onEventClick(player)}
              className="text-left"
            >
              <Stat
                icon={Trophy}
                label="Event Points"
                value={number(player.event)}
                accent
              />
            </button>
            <Stat icon={Clock3} label="Playtime" value={`${decimal(player.playtime)} hr`} />
            <Stat icon={Footprints} label="Distance" value={number(player.distance)} />
            <Stat icon={Zap} label="Raw Rating" value={decimal(player.raw_rating)} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* TOP THREE PODIUM WITH SQUARE AVATARS & HUGE RANKS */
function TopThree({ players, onPlayerClick }) {
  const top = players.slice(0, 3);
  if (!top.length) return null;

  const order = [top[1], top[0], top[2]];

  return (
    <div className="mb-8 grid grid-cols-1 items-end gap-4 md:grid-cols-3">
      {order.map((player, index) => {
        if (!player) return <div key={index} />;

        const actualRank = player.rank;
        const username = getUsername(player);

        const podium =
          actualRank === 1
            ? {
                icon: Crown,
                title: "CHAMPION",
                size: "h-28 w-28",
                border: "border-yellow-400/50",
                text: "text-yellow-300",
                heightClass: "md:mb-4", // Elevates Champion card visually in desktop without breaking transforms
              }
            : actualRank === 2
            ? {
                icon: Medal,
                title: "SECOND",
                size: "h-24 w-24",
                border: "border-slate-300/40",
                text: "text-slate-200",
                heightClass: "",
              }
            : {
                icon: Medal,
                title: "THIRD",
                size: "h-24 w-24",
                border: "border-orange-400/40",
                text: "text-orange-300",
                heightClass: "",
              };

        const Icon = podium.icon;

        return (
          <button
            key={username}
            onClick={() => onPlayerClick(player)}
            className={`group relative z-10 transition-transform duration-300 hover:z-20 hover:-translate-y-2 ${podium.heightClass} rounded-3xl border ${podium.border} bg-gradient-to-b from-white/[0.08] to-white/[0.02] p-5 text-center shadow-2xl`}
          >
            <div className={`mb-3 text-xs font-black tracking-[.25em] ${podium.text}`}>
              {podium.title}
            </div>

            <div className="relative mx-auto w-fit">
              {/* Square Avatar */}
              <img
                src={`https://mc-heads.net/avatar/${encodeURIComponent(
                  username
                )}/128`}
                alt={username}
                className={`${podium.size} rounded-2xl border-4 ${podium.border} shadow-2xl`}
              />

              {/* Big Prominent Rank Badge */}
              <div className="absolute -bottom-3 -right-3 grid h-10 w-10 place-items-center rounded-2xl border-2 border-white/20 bg-black text-base font-black text-yellow-400 shadow-xl">
                #{actualRank}
              </div>
            </div>

            <div className="mt-5 truncate text-xl font-black text-white">
              {username}
            </div>

            {player.prefix && (
              <div className="mt-1 text-[10px] uppercase tracking-widest text-white/40">
                {player.prefix}
              </div>
            )}

            <div className="mt-4 text-4xl font-black text-[#FFBF00]">
              {player.rating}
            </div>

            <div className="text-[10px] uppercase tracking-widest text-white/40">
              Rating / 100
            </div>

            <div className="mt-4 flex justify-center gap-5 text-xs text-white/50">
              <span>
                <b className="text-white">{number(player.kills)}</b> kills
              </span>

              <span>
                <b className="text-white">{decimal(player.kd)}</b> K/D
              </span>
            </div>

            <Icon
              size={18}
              className={`mx-auto mt-4 ${podium.text}`}
            />
          </button>
        );
      })}
    </div>
  );
}

export default function Leaderboards() {
  const [players, setPlayers] = useState([]);
  const [season, setSeason] = useState("LostDC SMP");
  const [totalPlayers, setTotalPlayers] = useState(0);
  const [maxRating, setMaxRating] = useState(0);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const [view, setView] = useState("cards");
  const [search, setSearch] = useState("");
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  const [eventDetailsPlayer, setEventDetailsPlayer] = useState(null);
  const [eventDetails, setEventDetails] = useState([]);
  const [eventDetailsLoading, setEventDetailsLoading] = useState(false);

  const [sort, setSort] = useState("rating");
  const [direction, setDirection] = useState("desc");

  const openEventDetails = async (player) => {
    setEventDetailsPlayer(player);
    setEventDetails([]);
    setEventDetailsLoading(true);

    try {
      const data = await fetchLeaderboardPlayerEvents(
        "lostdc-smp",
        "s9",
        getUsername(player)
      );

      setEventDetails(
        Array.isArray(data?.events) ? data.events : []
      );
    } catch (err) {
      console.error("Failed to load player event details:", err);
      setEventDetails([]);
    } finally {
      setEventDetailsLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await fetchLeaderboardSeason("lostdc-smp", "s9");

        if (!mounted) return;

        const entries = Array.isArray(response?.entries)
          ? response.entries
          : [];

        setPlayers(entries);
        setSeason(response?.season?.name || response?.season || "LostDC SMP");
        setTotalPlayers(response?.total_players || entries.length);
        setMaxRating(response?.max_rating || 0);
      } catch (err) {
        console.error("Failed to load LostDC leaderboard:", err);
        if (mounted) {
          setError("Unable to load the LostDC leaderboard right now.");
        }
      } finally {
        if (mounted) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, []);

  const refresh = async () => {
    try {
      setRefreshing(true);
      setError("");

      const response = await fetchLeaderboardSeason("lostdc-smp", "s9");
      const entries = Array.isArray(response?.entries)
        ? response.entries
        : [];

      setPlayers(entries);
      setSeason(response?.season?.name || response?.season || "LostDC SMP");
      setTotalPlayers(response?.total_players || entries.length);
      setMaxRating(response?.max_rating || 0);
    } catch (err) {
      console.error(err);
      setError("Failed to refresh leaderboard.");
    } finally {
      setRefreshing(false);
    }
  };

  const filteredPlayers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return players;

    return players.filter((player) => {
      const username = getUsername(player).toLowerCase();
      const prefix = String(player.prefix || "").toLowerCase();
      const tag = String(player.tag || "").toLowerCase();

      return (
        username.includes(query) ||
        prefix.includes(query) ||
        tag.includes(query)
      );
    });
  }, [players, search]);

  const sortedPlayers = useMemo(() => {
    const result = [...filteredPlayers];

    result.sort((a, b) => {
      const aValue = Number(a?.[sort] || 0);
      const bValue = Number(b?.[sort] || 0);

      return direction === "desc" ? bValue - aValue : aValue - bValue;
    });

    return result.map((player, index) => ({
      ...player,
      rank: index + 1,
    }));
  }, [filteredPlayers, sort, direction]);

  const cardPlayers = useMemo(() => {
    return [...filteredPlayers]
      .sort((a, b) => {
        const aPriority = getPriority(a.prefix);
        const bPriority = getPriority(b.prefix);

        if (aPriority !== bPriority) {
          return aPriority - bPriority;
        }

        return Number(b.rating || 0) - Number(a.rating || 0);
      })
      .map((player, index) => ({
        ...player,
        rank: index + 1,
      }));
  }, [filteredPlayers]);

  const topThree = useMemo(() => {
    return [...players]
      .sort((a, b) => Number(b.rating || 0) - Number(a.rating || 0))
      .map((player, index) => ({
        ...player,
        rank: index + 1,
      }))
      .slice(0, 3);
  }, [players]);

  const handleSort = (key) => {
    if (sort === key) {
      setDirection((current) => (current === "desc" ? "asc" : "desc"));
    } else {
      setSort(key);
      setDirection("desc");
    }
  };

  return (
    <div
      className="min-h-screen px-4 pb-24 pt-28 sm:px-6"
      data-testid="page-leaderboards"
    >
      <div className="mx-auto max-w-7xl">
        <SectionHeader
          eyebrow="LostDC Rankings"
          title="Legends of LostDC"
          subtitle="The ultimate LostDC player leaderboard — powered by live SMP statistics."
        />

        {/* Header Stats */}
        <div className="mb-8 grid grid-cols-2 gap-3 md:grid-cols-4">
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/40">
              <Users size={14} />
              Players
            </div>

            <div className="mt-2 text-2xl font-black text-white">
              {number(totalPlayers)}
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/40">
              <Trophy size={14} />
              Season
            </div>

            <div className="mt-2 truncate text-2xl font-black text-[#FFBF00]">
              {season}
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/40">
              <Activity size={14} />
              Rating
            </div>

            <div className="mt-2 text-2xl font-black text-white">
              {maxRating ? decimal(maxRating) : "—"}
            </div>
          </div>

          <button
            onClick={refresh}
            disabled={refreshing}
            className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-left transition hover:bg-white/[0.06] disabled:opacity-50"
          >
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/40">
              <RefreshCw
                size={14}
                className={refreshing ? "animate-spin" : ""}
              />
              Data
            </div>

            <div className="mt-2 text-sm font-bold text-white">
              {refreshing ? "Refreshing..." : "Refresh Rankings"}
            </div>
          </button>
        </div>

        {/* Top 3 */}
        {!loading && players.length > 0 && (
          <TopThree players={topThree} onPlayerClick={setSelectedPlayer} />
        )}

        {/* Controls */}
        <div className="mb-6 rounded-2xl border border-white/10 bg-black/30 p-3 backdrop-blur-xl">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="relative flex-1">
              <Search
                size={17}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30"
              />

              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search player, rank or title..."
                className="w-full rounded-xl border border-white/10 bg-white/[0.04] py-3 pl-11 pr-4 text-sm text-white outline-none placeholder:text-white/25 focus:border-yellow-400/40"
              />
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setView("cards")}
                className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-3 text-xs font-bold uppercase tracking-widest transition ${
                  view === "cards"
                    ? "bg-[#FFBF00] text-black"
                    : "bg-white/[0.05] text-white/60 hover:bg-white/[0.08]"
                }`}
              >
                <LayoutGrid size={15} />
                Cards
              </button>

              <button
                onClick={() => setView("table")}
                className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-3 text-xs font-bold uppercase tracking-widest transition ${
                  view === "table"
                    ? "bg-[#FFBF00] text-black"
                    : "bg-white/[0.05] text-white/60 hover:bg-white/[0.08]"
                }`}
              >
                <Table2 size={15} />
                Leaderboard
              </button>
            </div>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <div
                key={index}
                className="h-[360px] animate-pulse rounded-2xl border border-white/10 bg-white/[0.04]"
              />
            ))}
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="rounded-2xl border border-red-400/20 bg-red-400/10 p-6 text-center text-red-200">
            <div className="font-bold">{error}</div>

            <button
              onClick={refresh}
              className="mt-4 rounded-xl bg-red-400/20 px-5 py-2 text-xs font-bold uppercase tracking-widest hover:bg-red-400/30"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Empty */}
        {!loading && !error && sortedPlayers.length === 0 && (
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-12 text-center">
            <Search size={35} className="mx-auto mb-4 text-white/20" />

            <div className="font-bold text-white">No players found</div>

            <div className="mt-1 text-sm text-white/40">
              Try another player name or search term.
            </div>
          </div>
        )}

        {/* Cards View */}
        {!loading && !error && view === "cards" && cardPlayers.length > 0 && (
          <>
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-black text-white">Player Cards</h2>
                <p className="text-xs text-white/40">
                  Priority ranks first, then rating.
                </p>
              </div>

              <div className="text-xs text-white/40">
                {cardPlayers.length} players
              </div>
            </div>

            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
              {cardPlayers.map((player) => (
                <PlayerCard
                  key={getUsername(player)}
                  player={player}
                  onClick={setSelectedPlayer}
                  onEventClick={openEventDetails}
                />
              ))}
            </div>
          </>
        )}

        {/* Leaderboard View */}
        {!loading && !error && view === "table" && sortedPlayers.length > 0 && (
          <>
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-black text-white">Leaderboard</h2>
                <p className="text-xs text-white/40">
                  Select a category bubble to re-rank players.
                </p>
              </div>

              <div className="text-xs text-white/40">
                Sorted by{" "}
                {LEADERBOARD_CATEGORIES.find((s) => s.key === sort)?.label}
              </div>
            </div>

            <LeaderboardBubbleList
              players={sortedPlayers}
              onPlayerClick={setSelectedPlayer}
              sort={sort}
              direction={direction}
              onSort={handleSort}
            />
          </>
        )}
      </div>

      <PlayerModal
        player={selectedPlayer}
        onClose={() => setSelectedPlayer(null)}
        onEventClick={openEventDetails}
      />

      <EventPointsModal
        player={eventDetailsPlayer}
        events={eventDetails}
        loading={eventDetailsLoading}
        onClose={() => setEventDetailsPlayer(null)}
      />
    </div>
  );
}